import { useRef, useState } from 'react';
import { fmtTime } from '../../shared/dates';
import { DayLabels, ResourceTypeLabels, ServiceLabels } from '@census/shared';
import type { AiSuggestionAction } from '@census/shared';
import { ScheduleGrid } from '../schedule/ScheduleGrid';
import { AiSchedulingChecklistContent } from './AiSchedulingChecklistModal';

interface PreviewData {
  clientDraft: any;
  groupEnrollments: any[];
  proposedAppointments: any[];
  decisionTraces: any[];
  resourceAudits: any[];
  violations: any[];
  unscheduledServices: string[];
}

interface ServiceConstraintsProp {
  excludedDaysByService: Record<string, string[]>;
  excludedResourcesByService: Record<string, string[]>;
  pinnedDayByService: Record<string, string>;
  pinnedResourceByService: Record<string, string>;
  avoidOverlapServices?: string[];
}

interface IntakePreviewProps {
  data: PreviewData;
  serviceConstraints?: ServiceConstraintsProp;
  // Returns true when the actions were actually applied (engine accepted them
  // without regressing) and false when they were rejected. The modal uses the
  // return value to decide whether to close itself.
  onApplySuggestions?: (actions: AiSuggestionAction[]) => Promise<boolean>;
  onClearConstraint?: (
    kind: 'excludedDay' | 'excludedResource' | 'avoidOverlap',
    serviceType: string,
    value: string,
  ) => void;
  // Pre-validates whether an AI-suggested action could be applied without
  // regressing the schedule. The checklist uses this to HIDE suggestions
  // the engine cannot actually act on.
  validateApplicability?: (action: AiSuggestionAction) => Promise<boolean>;
  previewEpoch?: number;
  onAiStatusChange?: (complete: boolean) => void;
  aiSectionRef?: React.RefObject<HTMLElement | null>;
}

export function IntakePreview({
  data,
  serviceConstraints,
  onApplySuggestions,
  onClearConstraint,
  validateApplicability,
  previewEpoch,
  onAiStatusChange,
  aiSectionRef,
}: IntakePreviewProps) {

  // Build a list of active constraint chips. Resource-id chips look up the
  // provider's display name from the current preview's resourceAudits.
  const constraintChips: Array<
    | { kind: 'excludedDay'; serviceType: string; day: string; label: string }
    | { kind: 'excludedResource'; serviceType: string; resourceId: string; label: string }
    | { kind: 'avoidOverlap'; serviceType: string; value: string; label: string }
  > = [];
  if (serviceConstraints) {
    for (const [svc, days] of Object.entries(serviceConstraints.excludedDaysByService ?? {})) {
      for (const d of days ?? []) {
        const svcLabel = (ServiceLabels as Record<string, string>)[svc] ?? svc;
        const dayLabel = (DayLabels as Record<string, string>)[d] ?? d;
        constraintChips.push({
          kind: 'excludedDay',
          serviceType: svc,
          day: d,
          label: `${svcLabel} not on ${dayLabel}`,
        });
      }
    }
    for (const [svc, ids] of Object.entries(serviceConstraints.excludedResourcesByService ?? {})) {
      for (const rid of ids ?? []) {
        const audit = data.resourceAudits.find((a: any) => a.resourceId === rid);
        const svcLabel = (ServiceLabels as Record<string, string>)[svc] ?? svc;
        constraintChips.push({
          kind: 'excludedResource',
          serviceType: svc,
          resourceId: rid,
          label: `${svcLabel} not with ${audit?.resourceName ?? rid.slice(0, 8)}`,
        });
      }
    }
    for (const svc of serviceConstraints.avoidOverlapServices ?? []) {
      const svcLabel = (ServiceLabels as Record<string, string>)[svc] ?? svc;
      constraintChips.push({
        kind: 'avoidOverlap',
        serviceType: svc,
        value: svc,
        label: `${svcLabel} auto-fit (no overlap)`,
      });
    }
  }

  const groupBlocks = data.groupEnrollments.flatMap((g) =>
    g.days.map((d: any) => ({
      day: d,
      startMin: g.startMin,
      endMin: g.endMin,
      label: ServiceLabels[g.serviceType as 'PHP'],
      sub: g.displayName,
      kind: 'GROUP' as const,
    })),
  );
  const indBlocks = data.proposedAppointments.map((a) => ({
    day: a.day,
    startMin: a.startMin,
    endMin: a.endMin,
    label: ServiceLabels[a.serviceType as 'THERAPY'],
    sub: a.resourceName ? `with ${a.resourceName}` : '',
    kind: 'INDIVIDUAL' as const,
  }));
  const blocks = [...groupBlocks, ...indBlocks];

  const hard = data.violations.filter((v) => v.severity === 'HARD');
  const policy = data.violations.filter((v) => v.severity === 'POLICY');
  const soft = data.violations.filter((v) => v.severity === 'SOFT');

  return (
    <>
    <div className="grid gap-4 lg:grid-cols-12">
      <section className="card lg:col-span-7 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold">
              Proposed week for {data.clientDraft.cohortId} — {data.clientDraft.firstName} {data.clientDraft.lastName}
            </h2>
            <p className="text-xs text-slate-500">
              Cohort {data.clientDraft.cohort} · IOP: {(data.clientDraft as any).iopTrackName ?? '—'} · Nursing: {data.clientDraft.nursingGroupDay ?? '—'}
            </p>
          </div>
        </div>
        {constraintChips.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-2xs font-semibold uppercase tracking-wider text-violet-500">
              AI overrides:
            </span>
            {constraintChips.map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-2xs text-violet-700"
                title="Applied from an AI suggestion — click × to remove and re-run"
              >
                {c.label}
                {onClearConstraint && (
                  <button
                    type="button"
                    className="ml-0.5 text-violet-500 hover:text-violet-900"
                    onClick={() =>
                      onClearConstraint(
                        c.kind,
                        c.serviceType,
                        c.kind === 'excludedDay'
                          ? c.day
                          : c.kind === 'excludedResource'
                            ? c.resourceId
                            : c.value,
                      )
                    }
                    aria-label="Remove constraint"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3">
          <ScheduleGrid blocks={blocks} />
        </div>
      </section>

      <section className="space-y-4 lg:col-span-5">
        <div className="card p-4">
          <h3 className="text-sm font-semibold">Conflicts &amp; warnings</h3>
          <div className="mt-2 space-y-2 text-sm">
            <Line color="rose" count={hard.length} label="Hard (block commit)" />
            <Line color="amber" count={policy.length} label="Policy (admin override w/ reason)" />
            <Line color="slate" count={soft.length} label="Soft (informational)" />
          </div>
          <ul className="mt-3 space-y-2 text-xs">
            {[...hard, ...policy, ...soft].map((v, i) => (
              <li key={i}>
                <span className={v.severity === 'HARD' ? 'pill-danger' : v.severity === 'POLICY' ? 'pill-warn' : 'pill-info'}>
                  {v.severity}
                </span>
                <span className="ml-2 text-slate-700">{v.message}</span>
              </li>
            ))}
          </ul>
          {data.unscheduledServices.length > 0 && (
            <p className="mt-3 rounded bg-rose-50 px-3 py-2 text-xs text-rose-700">
              Unscheduled services: {data.unscheduledServices.join(', ')}
            </p>
          )}
        </div>

        <div className="card p-4">
          <h3 className="text-sm font-semibold">Resource audit</h3>
          <ul className="mt-2 space-y-3 text-sm">
            {data.resourceAudits.map((a) => (
              <li key={a.resourceId} className="border-b border-slate-100 pb-2 last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{a.resourceName}</span>
                  <span className="pill-muted">{ResourceTypeLabels[a.resourceType as 'PROVIDER']}</span>
                </div>
                {a.weeklyMinutesCap > 0 ? (
                  <p className="text-xs text-slate-500">
                    Util:{' '}
                    <span
                      className={
                        a.utilizationAfter > 95
                          ? 'pill-danger'
                          : a.utilizationAfter > 90
                            ? 'pill-warn'
                            : 'pill-info'
                      }
                    >
                      {a.utilizationBefore.toFixed(0)}% → {a.utilizationAfter.toFixed(0)}%
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">Capacity {a.capacity}</p>
                )}
                <ul className="mt-1 text-xs text-slate-500">
                  {a.proposedSlots.map((s: any, i: number) => (
                    <li key={i}>
                      {DayLabels[s.day as 'MON']} {fmtTime(s.startMin)}–{fmtTime(s.endMin)} ·{' '}
                      {ServiceLabels[s.serviceType as 'THERAPY']}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>

        <DecisionTraceList traces={data.decisionTraces} />
      </section>
    </div>
    {/* Inline AI scheduling checklist — replaces the old "✨ Ask AI" modal.
        The checklist (AI narrative + structured suggestions with Apply
        buttons, group/provider/conflict checks, engine recommendation) is
        now shown directly on the preview screen so the operator sees
        everything at once. */}
    <section ref={aiSectionRef as React.RefObject<HTMLElement>} className="mt-4 card p-4">
      <h2 className="mb-3 text-base font-semibold text-ink">AI Scheduling Checklist</h2>
      <AiSchedulingChecklistContent
        preview={data}
        serviceConstraints={serviceConstraints}
        onApplySuggestions={onApplySuggestions}
        validateApplicability={validateApplicability}
        previewEpoch={previewEpoch}
        onAiStatusChange={onAiStatusChange}
      />
    </section>
    </>
  );
}

function Line({ color, count, label }: { color: string; count: number; label: string }) {
  const cls = `pill-${color === 'rose' ? 'danger' : color === 'amber' ? 'warn' : 'info'}`;
  return (
    <div className="flex items-center gap-2">
      <span className={cls}>{count}</span>
      <span className="text-slate-700">{label}</span>
    </div>
  );
}

function DecisionTraceList({ traces }: { traces: any[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card p-4">
      <button className="flex w-full items-center justify-between text-left" onClick={() => setOpen((o) => !o)}>
        <h3 className="text-sm font-semibold">Decision trace</h3>
        <span className="text-xs text-slate-500">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <ul className="mt-3 space-y-3 text-xs">
          {traces.map((t, i) => (
            <li key={i} className="border-b border-slate-100 pb-2 last:border-0">
              <div className="flex items-center justify-between">
                <span className="font-medium">{ServiceLabels[t.serviceType as 'THERAPY']}</span>
                <span className={t.placed ? 'pill-success' : 'pill-danger'}>
                  {t.placed ? 'placed' : 'unplaced'}
                </span>
              </div>
              {t.placed && (
                <p className="text-slate-500">
                  {DayLabels[t.day as 'MON']} {fmtTime(t.startMin)}–{fmtTime(t.endMin)} · {t.resourceName}
                </p>
              )}
              <ul className="ml-4 list-disc text-slate-500">
                {t.reasons.map((r: string, j: number) => (
                  <li key={j}>{r}</li>
                ))}
              </ul>
              <p className="mt-1 text-slate-400">
                Alternatives considered: {t.alternativesConsidered}
                {t.score !== undefined && ` · score ${t.score.toFixed(1)}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
